import { FormEvent, useEffect, useState } from 'react';
import { ApiError, CurrentUser, UserSummary, api } from '../lib/api';
import { useI18n } from '../i18n';

type Props = {
  currentUser: CurrentUser;
  onClose: () => void;
};

type Editor = {
  user: UserSummary;
  displayName: string;
  role: 'ADMIN' | 'USER';
  enabled: boolean;
};

export function UsersPage({ currentUser, onClose }: Props) {
  const { t, locale } = useI18n();
  const [users, setUsers] = useState<UserSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [form, setForm] = useState({ username: '', displayName: '', password: '', role: 'USER' as 'ADMIN' | 'USER' });
  const [editor, setEditor] = useState<Editor>();
  const [resetUser, setResetUser] = useState<UserSummary>();
  const [resetPassword, setResetPassword] = useState('');

  const readableError = (reason: unknown) => reason instanceof ApiError
    ? reason.message
    : reason instanceof Error
      ? reason.message
      : t('common.operationFailed');

  const formatDate = (value?: string) => {
    if (!value) return t('common.neverLogin');
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString(locale);
  };

  async function load() {
    setLoading(true);
    try {
      setUsers(await api<UserSummary[]>('/users'));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load().catch((reason) => setError(readableError(reason)));
  }, []);

  async function create(event: FormEvent) {
    event.preventDefault();
    setWorking(true);
    setError('');
    setNotice('');
    try {
      const created = await api<UserSummary>('/users', {
        method: 'POST',
        body: JSON.stringify(form),
      });
      setForm({ username: '', displayName: '', password: '', role: 'USER' });
      setNotice(`${created.username} ${created.role === 'ADMIN' ? t('account.admin') : t('account.user')}`);
      await load();
    } catch (reason) {
      setError(readableError(reason));
    } finally {
      setWorking(false);
    }
  }

  async function saveEditor(event: FormEvent) {
    event.preventDefault();
    if (!editor) return;
    setWorking(true);
    setError('');
    try {
      await api<UserSummary>(`/users/${editor.user.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          displayName: editor.displayName,
          role: editor.role,
          enabled: editor.enabled,
          updatedAt: editor.user.updatedAt,
        }),
      });
      setEditor(undefined);
      setNotice(t('account.profileUpdated'));
      await load();
    } catch (reason) {
      setError(readableError(reason));
    } finally {
      setWorking(false);
    }
  }

  async function reset(event: FormEvent) {
    event.preventDefault();
    if (!resetUser) return;
    setWorking(true);
    setError('');
    try {
      await api<void>(`/users/${resetUser.id}/reset-password`, {
        method: 'POST',
        body: JSON.stringify({ password: resetPassword }),
      });
      setNotice(`${t('users.resetPassword')}: ${resetUser.username}`);
      setResetUser(undefined);
      setResetPassword('');
    } catch (reason) {
      setError(readableError(reason));
    } finally {
      setWorking(false);
    }
  }

  async function remove(user: UserSummary) {
    if (!window.confirm(`${t('common.delete')} ${user.username}?`)) return;
    setWorking(true);
    setError('');
    try {
      await api<void>(`/users/${user.id}`, { method: 'DELETE' });
      setNotice(`${t('common.delete')}: ${user.username}`);
      await load();
    } catch (reason) {
      setError(readableError(reason));
    } finally {
      setWorking(false);
    }
  }

  return (
    <div className="modal-backdrop users-management-backdrop" onMouseDown={onClose}>
      <section className="users-management-panel" onMouseDown={(event) => event.stopPropagation()}>
        <header className="users-management-header">
          <div><p className="eyebrow">{t('users.eyebrow')}</p><h2>{t('users.title')}</h2><span>{t('users.subtitle')}</span></div>
          <button onClick={onClose}>{t('common.close')}</button>
        </header>

        {error && <div className="notice error-notice">{error}</div>}
        {notice && <div className="notice success-notice">{notice}</div>}

        <form className="user-create-form user-create-grid" onSubmit={create}>
          <input value={form.username} onChange={(event) => setForm({ ...form, username: event.target.value })} placeholder={t('users.username')} required />
          <input value={form.displayName} onChange={(event) => setForm({ ...form, displayName: event.target.value })} placeholder={t('users.displayName')} required />
          <input type="password" value={form.password} onChange={(event) => setForm({ ...form, password: event.target.value })} placeholder={t('users.initialPassword')} required />
          <select value={form.role} onChange={(event) => setForm({ ...form, role: event.target.value as 'ADMIN' | 'USER' })}>
            <option value="USER">{t('account.user')}</option>
            <option value="ADMIN">{t('account.admin')}</option>
          </select>
          <button className="primary-button" type="submit" disabled={working}>{t('users.create')}</button>
        </form>

        <div className="catalog-table-wrap users-table-wrap">
          <table className="catalog-table users-table">
            <thead><tr><th>{t('users.user')}</th><th>{t('users.role')}</th><th>{t('users.status')}</th><th>{t('users.lastLogin')}</th><th>{t('users.createdAt')}</th><th>{t('users.actions')}</th></tr></thead>
            <tbody>
              {loading && <tr><td colSpan={6} className="catalog-empty">{t('users.loading')}</td></tr>}
              {!loading && users.map((user) => (
                <tr key={user.id}>
                  <td><strong>{user.displayName}</strong><span>@{user.username}{user.id === currentUser.id ? ` · ${t('account.currentUser')}` : ''}</span></td>
                  <td><span className="source-type-badge">{user.role === 'ADMIN' ? t('account.admin') : t('account.user')}</span></td>
                  <td><span className={`catalog-status ${user.enabled ? 'online' : 'offline'}`}>{user.enabled ? t('common.enabled') : t('common.disabled')}</span></td>
                  <td>{formatDate(user.lastLoginAt)}</td>
                  <td>{formatDate(user.createdAt)}</td>
                  <td className="catalog-actions">
                    <button onClick={() => setEditor({ user, displayName: user.displayName, role: user.role, enabled: user.enabled })}>{t('common.edit')}</button>
                    <button onClick={() => { setResetUser(user); setResetPassword(''); }}>{t('users.resetPassword')}</button>
                    {user.id !== currentUser.id && <button className="danger-text" onClick={() => void remove(user)}>{t('common.delete')}</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {editor && (
        <div className="modal-backdrop nested-modal" onMouseDown={() => setEditor(undefined)}>
          <form className="catalog-modal" onSubmit={saveEditor} onMouseDown={(event) => event.stopPropagation()}>
            <header><div><p className="eyebrow">EDIT USER</p><h3>{editor.user.username}</h3></div><button type="button" onClick={() => setEditor(undefined)}>{t('common.close')}</button></header>
            <label>{t('users.displayName')}<input value={editor.displayName} maxLength={128} onChange={(event) => setEditor({ ...editor, displayName: event.target.value })} required /></label>
            <label>{t('users.role')}<select value={editor.role} disabled={editor.user.id === currentUser.id} onChange={(event) => setEditor({ ...editor, role: event.target.value as 'ADMIN' | 'USER' })}><option value="USER">{t('account.user')}</option><option value="ADMIN">{t('account.admin')}</option></select></label>
            <label className="checkbox-label"><input type="checkbox" checked={editor.enabled} disabled={editor.user.id === currentUser.id} onChange={(event) => setEditor({ ...editor, enabled: event.target.checked })} /><span>{t('users.enableUser')}</span></label>
            {editor.user.id === currentUser.id && <p className="muted form-hint">{t('users.currentRestriction')}</p>}
            <footer><button type="button" onClick={() => setEditor(undefined)}>{t('common.cancel')}</button><button className="primary-button" disabled={working}>{t('common.save')}</button></footer>
          </form>
        </div>
      )}

      {resetUser && (
        <div className="modal-backdrop nested-modal" onMouseDown={() => setResetUser(undefined)}>
          <form className="catalog-modal" onSubmit={reset} onMouseDown={(event) => event.stopPropagation()}>
            <header><div><p className="eyebrow">RESET PASSWORD</p><h3>{resetUser.username}</h3></div><button type="button" onClick={() => setResetUser(undefined)}>{t('common.close')}</button></header>
            <label>{t('account.newPassword')}<input type="password" value={resetPassword} onChange={(event) => setResetPassword(event.target.value)} required /></label>
            <p className="muted form-hint">{t('users.passwordNotEmpty')}</p>
            <footer><button type="button" onClick={() => setResetUser(undefined)}>{t('common.cancel')}</button><button className="primary-button" disabled={working}>{t('users.resetPassword')}</button></footer>
          </form>
        </div>
      )}
    </div>
  );
}
